import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { PageHeader, useToast } from '@platform/ui';

import { AuditTab, DEFAULT_AUDIT_FILTERS, type AuditFilters } from '../../features/logs/audit-tab';
import { ErrorsTab } from '../../features/logs/errors-tab';
import { dayBoundary } from '../../features/logs/filter-controls';
import {
  DEFAULT_CALL_FILTERS,
  IntegrationCallsTab,
  type CallFilters,
} from '../../features/logs/integration-calls-tab';
import { QueuesTab } from '../../features/logs/queues-tab';
import { RequestsTab } from '../../features/logs/requests-tab';
import { tenantOptionsKey } from '../../features/tenants/use-tenant';
import {
  PLATFORM_ENDPOINT_PERMISSIONS,
  type DeadLetterJob,
  type RequestRange,
} from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';
import { RequirePermission } from '../../lib/session/guards';
import { PLATFORM_PERMISSIONS, usePermissions } from '../../lib/session/permissions';
import { useStepUp } from '../../lib/session/step-up';

const TABS = ['audit', 'calls', 'queues', 'requests', 'errors'] as const;
export type LogsTab = (typeof TABS)[number];

const TAB_LABELS: Record<LogsTab, string> = {
  audit: 'Audit',
  calls: 'Integration calls',
  queues: 'Queues',
  requests: 'Requests',
  errors: 'Errors',
};

export const Route = createFileRoute('/logs/')({
  /**
   * The tab lives in the URL rather than in state, so a support engineer can send a
   * colleague straight to the queues (the same reasoning as the tenant tabs). An unknown
   * or absent value falls back to Audit rather than rendering nothing.
   */
  validateSearch: (search: Record<string, unknown>): { tab: LogsTab } => {
    const tab = search['tab'];
    return {
      tab:
        typeof tab === 'string' && (TABS as readonly string[]).includes(tab)
          ? (tab as LogsTab)
          : 'audit',
    };
  },
  component: PlatformLogsRoute,
});

/**
 * `/logs` (FR-SUPER-05, FR-LOG-*, MASTER_PLAN M12.4, ADR-020).
 *
 * Five tabs, one seam. Since M12.4 every one of them is a real endpoint: `logs/audit`,
 * `integrations/calls`, `logs/jobs` with its dead letters, `logs/requests` and
 * `logs/errors`. The tab decides which query is enabled, so opening Queues does not
 * fetch eighty audit rows nobody asked for.
 *
 * The tabs are marked up as a `<nav>` with `aria-current`, not as an ARIA tablist: they
 * change the URL and load different data, which is a set of links, not a tab widget.
 */
function PlatformLogsRoute() {
  return (
    <RequirePermission keys={[PLATFORM_PERMISSIONS.logsRead]}>
      <PlatformLogsPage />
    </RequirePermission>
  );
}

function PlatformLogsPage() {
  const { tab } = Route.useSearch();
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { guard } = useStepUp();
  const { toast } = useToast();

  const [auditFilters, setAuditFilters] = useState<AuditFilters>(DEFAULT_AUDIT_FILTERS);
  const [callFilters, setCallFilters] = useState<CallFilters>(DEFAULT_CALL_FILTERS);
  const [range, setRange] = useState<RequestRange>('24h');
  const [jobError, setJobError] = useState<string | undefined>(undefined);
  const [busyJobId, setBusyJobId] = useState<string | null>(null);

  const tenants = useQuery({
    queryKey: tenantOptionsKey(),
    queryFn: async () => (await client.listTenants({ limit: 100 })).data,
  });
  const tenantOptions = (tenants.data ?? []).map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
  }));

  /* ------------------------------------------------------------------ audit */

  const audit = useInfiniteQuery({
    queryKey: ['platform-audit', auditFilters],
    queryFn: ({ pageParam }) =>
      client.listAudit({
        tenantId: auditFilters.tenantId,
        actorId: auditFilters.actorId,
        action: auditFilters.action,
        from: dayBoundary(auditFilters.from, 'from'),
        to: dayBoundary(auditFilters.to, 'to'),
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
    enabled: tab === 'audit',
  });

  const auditRows = (audit.data?.pages ?? []).flatMap((page) => page.data);
  /*
   * `actorId` is nullable - a row written by a job has no person behind it - and "system"
   * is not something an operator can filter by, so those rows are left out of the select
   * rather than offered as an id of `null`.
   */
  const actors = [
    ...new Map(
      auditRows
        .filter((row): row is typeof row & { actorId: string } => row.actorId !== null)
        .map((row) => [row.actorId, row.actorLabel]),
    ).entries(),
  ].map(([id, label]) => ({ id, label }));

  /* ------------------------------------------------------- integration calls */

  const callPages = useInfiniteQuery({
    queryKey: ['platform-integration-calls', callFilters],
    queryFn: ({ pageParam }) =>
      client.listIntegrationCalls({
        tenantId: callFilters.tenantId,
        integrationKey: callFilters.integrationKey,
        status: callFilters.status === 'all' ? undefined : (callFilters.status as never),
        from: dayBoundary(callFilters.from, 'from'),
        to: dayBoundary(callFilters.to, 'to'),
        limit: 25,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.nextCursor,
    enabled: tab === 'calls',
  });

  const catalog = useQuery({
    queryKey: ['platform-catalog'],
    queryFn: () => client.listCatalog(),
    enabled: tab === 'calls',
  });

  /* ----------------------------------------------------------------- queues */

  const queues = useQuery({
    queryKey: ['platform-queues'],
    queryFn: () => client.listQueues(),
    enabled: tab === 'queues',
  });
  const deadLetters = useQuery({
    queryKey: ['platform-dead-letters'],
    queryFn: () => client.listDeadLetters(),
    enabled: tab === 'queues',
  });

  const refreshQueues = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['platform-queues'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-dead-letters'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-health'] });
  };

  const jobAction = useMutation({
    mutationFn: ({ run }: { run: () => Promise<unknown>; message: string }) =>
      guard(run, 'act on a dead-lettered job'),
    onSuccess: async (_result, variables) => {
      setJobError(undefined);
      setBusyJobId(null);
      toast({ tone: 'success', message: variables.message });
      await refreshQueues();
    },
    onError: (error: Error) => {
      setBusyJobId(null);
      setJobError(error.message);
    },
  });

  /* --------------------------------------------------------------- requests */

  const requests = useQuery({
    queryKey: ['platform-requests', range],
    queryFn: () => client.getRequestSummary(range),
    enabled: tab === 'requests',
  });

  /* ----------------------------------------------------------------- errors */

  const errors = useQuery({
    queryKey: ['platform-errors', range],
    queryFn: () => client.listErrorGroups(range),
    enabled: tab === 'errors',
  });
  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => client.getSettings(),
    enabled: tab === 'errors',
  });

  return (
    <>
      <PageHeader
        title="Logs"
        description="The cross-tenant audit trail, vendor calls, the job queues and their dead letters, request metrics and the error summary."
      />

      <nav aria-label="Log views" className="mb-6 flex flex-wrap gap-1 border-b border-neutral-200">
        {TABS.map((key) => (
          <Link
            key={key}
            to="/logs"
            search={{ tab: key }}
            aria-current={tab === key ? 'page' : undefined}
            className={
              tab === key
                ? 'border-b-2 border-primary px-4 py-2 text-base font-semibold text-primary-accessible'
                : 'border-b-2 border-transparent px-4 py-2 text-base text-neutral-500 hover:text-ink'
            }
          >
            {TAB_LABELS[key]}
          </Link>
        ))}
      </nav>

      {tab === 'audit' ? (
        <AuditTab
          rows={auditRows}
          tenants={tenantOptions}
          actors={actors}
          filters={auditFilters}
          onFiltersChange={setAuditFilters}
          hasMore={audit.hasNextPage}
          loadingMore={audit.isFetchingNextPage}
          onLoadMore={() => {
            void audit.fetchNextPage();
          }}
          pending={audit.isPending}
          error={audit.isError ? 'The audit trail could not be loaded.' : undefined}
        />
      ) : null}

      {tab === 'calls' ? (
        <IntegrationCallsTab
          calls={(callPages.data?.pages ?? []).flatMap((page) => page.data)}
          tenants={tenantOptions}
          integrations={(catalog.data ?? []).map((entry) => ({
            key: entry.key,
            name: entry.name,
          }))}
          filters={callFilters}
          onFiltersChange={setCallFilters}
          hasMore={callPages.hasNextPage}
          loadingMore={callPages.isFetchingNextPage}
          onLoadMore={() => {
            void callPages.fetchNextPage();
          }}
          pending={callPages.isPending}
          error={callPages.isError ? (callPages.error as Error).message : undefined}
        />
      ) : null}

      {tab === 'queues' ? (
        <QueuesTab
          queues={queues.data ?? []}
          deadLetters={deadLetters.data ?? []}
          tenants={tenantOptions}
          pending={queues.isPending || deadLetters.isPending}
          canAct={can(PLATFORM_ENDPOINT_PERMISSIONS.jobsManage)}
          busyJobId={busyJobId}
          error={
            jobError ??
            (queues.isError || deadLetters.isError ? 'The queues could not be loaded.' : undefined)
          }
          onRedrive={(job: DeadLetterJob, reason: string) => {
            setBusyJobId(job.id);
            jobAction.mutate({
              run: () => client.redriveJob(job.id, { reason }),
              message: `${job.jobName} is back on the ${job.sourceQueue} queue.`,
            });
          }}
          onDiscard={(job: DeadLetterJob, reason: string) => {
            setBusyJobId(job.id);
            jobAction.mutate({
              run: () => client.discardJob(job.id, { reason }),
              message: `${job.jobName} was discarded.`,
            });
          }}
        />
      ) : null}

      {tab === 'requests' ? (
        <RequestsTab
          summary={requests.data ?? null}
          range={range}
          onRangeChange={setRange}
          pending={requests.isPending}
          error={requests.isError ? 'Request metrics could not be loaded.' : undefined}
        />
      ) : null}

      {tab === 'errors' ? (
        <ErrorsTab
          groups={errors.data ?? []}
          pending={errors.isPending || settings.isPending}
          sentryUrl={settings.data?.sentryUrl ?? null}
          error={errors.isError ? 'The error summary could not be loaded.' : undefined}
        />
      ) : null}
    </>
  );
}
